import { DEFAULT_LOCALE, type Locale } from '@infiltre/shared';

/**
 * Generate a throwaway display name nodding to famous cops & crooks from each
 * locale's pop culture — classic detectives, iconic outlaws, screen villains.
 * Used as the last-resort fallback when joining a room with no Discord
 * identity and no previously saved name. The user lands in the lobby and can
 * rename themselves later from the player list.
 */
const POOLS = {
  en: [
    'Sherlock',
    'Watson',
    'Hercule Poirot',
    'Miss Marple',
    'Columbo',
    'Marlowe',
    'Sam Spade',
    'Mike Hammer',
    'Bond',
    'Bourne',
    'Magnum',
    'Kojak',
    'Starsky',
    'Hutch',
    'Dirty Harry',
    'Serpico',
    'Drebin',
    'Crockett',
    'Tubbs',
    'McNulty',
    'Omar',
    'Stringer',
    'Cohle',
    'Sipowicz',
    'Briscoe',
    'Benson',
    'Bosch',
    'Reacher',
    'McClane',
    'Riggs',
    'Murtaugh',
    'Heisenberg',
    'Pinkman',
    'Tony Soprano',
    'Don Corleone',
    'Michael Corleone',
    'Tony Montana',
    'Hannibal',
    'Dexter',
    'Keyser Söze',
    'Tyler Durden',
    'Verbal Kint',
    'Anton Chigurh',
    'Norman Bates',
    'Patrick Bateman',
    'Joker',
    'Moriarty',
    'Lex Luthor',
    'Capone',
    'Bonnie',
    'Clyde',
    'Lucky Luciano',
    'John Gotti',
    'Bugsy',
    'Henry Hill',
    'Whitey Bulger',
  ],
  fr: [
    'Maigret',
    'Navarro',
    'Julie Lescaut',
    'Moulin',
    'Nestor Burma',
    'San-Antonio',
    'Bourrel',
    'Cordier',
    'Émilien',
    'Daniel',
    'Capitaine Marleau',
    'Astrid',
    'Raphaëlle',
    'Caïn',
    'Laure Berthaud',
    'Gilou',
    'Malotru',
    'Marie-Jeanne',
    'Henri Duflot',
    'Inspecteur Gadget',
    'Inspecteur Clouseau',
    'OSS 117',
    'Hubert Bonisseur de la Bath',
    'Eddy Caplan',
    'Walter Morlighem',
    'Roxanne Delgado',
    'Vidocq',
    'Mesrine',
    'Cartouche',
    'Mandrin',
    'Arsène Lupin',
    'Fantômas',
    'Pierrot le Fou',
    'Spaggiari',
    'Buisson',
    'Roch Siffredi',
    'Tony Zampa',
    'Bob le Flambeur',
    'Jef Costello',
    'Tony Arzenta',
    'Le Samouraï',
    'Le Dabe',
    'Raoul Volfoni',
    'Fernand Naudin',
    'Charlie Bauer',
    'René La Canne',
    'Bonnot',
    'Belmondo',
    'Ventura',
    'Delon',
  ],
  es: [
    'Pepe Carvalho',
    'Plinio',
    'Méndez',
    'Petra Delicado',
    'Bevilacqua',
    'Chamorro',
    'Vargas',
    'Torrente',
    'Curro Jiménez',
    'El Profesor',
    'Berlín',
    'Tokio',
    'Helsinki',
    'Nairobi',
    'Río',
    'Denver',
    'Oslo',
    'Bogotá',
    'Marsella',
    'Estocolmo',
    'Manila',
    'Palermo',
    'Escobar',
    'Don Vito',
    'Tony Montana',
    'Sicario',
    'El Chapo',
    'El Lute',
    'Pancho Villa',
    'El Vaquilla',
    'Loquillo',
    'Pacino',
  ],
  de: [
    'Schimanski',
    'Derrick',
    'Tschiller',
    'Borowski',
    'Faber',
    'Boerne',
    'Thiel',
    'Lürsen',
    'Lannert',
    'Bootz',
    'Ballauf',
    'Schenk',
    'Batic',
    'Leitmayr',
    'Odenthal',
    'Kopper',
    'Kress',
    'Kommissar Rex',
    'Cobra',
    'Der Alte',
    'Wilsberg',
    'Stromberg',
    'Heisenberg',
    'Hagen',
    'Schinderhannes',
    'Räuber Hotzenplotz',
    'Adler',
    'Schmitz',
    'Pinkus',
    'Bull',
  ],
  pt: [
    'Capitão Nascimento',
    'Matias',
    'André Matias',
    'Zé Pequeno',
    'Buscapé',
    'Bené',
    'Mané Galinha',
    'Cabeleira',
    'Mandrake',
    'Lampião',
    'Cangaceiro',
    'Mineirinho',
    'Marcola',
    'Inspetor Faro',
    'Comissário',
    'Ramiro',
    'Mendonça',
    'Cabo Anselmo',
    'Pixote',
    'Tony Ramos',
    'Lúcio Flávio',
    'Capitão Fábio',
    'Capitão Nogueira',
    'Detetive Mané',
  ],
} as const satisfies Record<Locale, readonly string[]>;

const pick = <T>(xs: readonly T[]): T => xs[Math.floor(Math.random() * xs.length)] as T;

export const randomGuestName = (locale: Locale = DEFAULT_LOCALE): string =>
  pick(POOLS[locale] ?? POOLS[DEFAULT_LOCALE]);
